import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  InventoryItemsModalMode,
  InventoryTagData,
} from './_InventoryItemsModal';
import { InventoryItemsModalItemsGrid } from './InventoryItemsModalItemsGrid';
import { InventoryTagOption } from './InventoryItemsModalTagsGrid';
import { orderEntitiesByIds } from './orderedIds';

import {
  $touchModalItemFavoritesByGroup,
  $touchModalItemOrderByGroup,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';

export type InventoryItemsModalGroupViewProps = {
  mode: InventoryItemsModalMode;
  selectedGroupId: string;
  selectedGroupName: string;

  // Items data
  itemsForGroup: InventoryItemRecord[];

  // Tags mode data
  tagsForGroup: InventoryTagOption[];
  inventoryTagOptionsByItemId?: Map<string, unknown[]>;

  // Selection state
  selectedId?: string | null;

  // Highlighted SO materials
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;

  // Callbacks
  onSelectMaterial: (materialId: string) => void;
  onSelectItem?: (itemId: string) => void;
  onSelectTag: (tagId: string, tagData: InventoryTagData) => void;
};

export const InventoryItemsModalGroupView = ({
  mode,
  selectedGroupId,
  selectedGroupName,
  itemsForGroup,
  inventoryTagOptionsByItemId,
  selectedId,
  highlightedMaterialIds,
  highlightedSectionTitle,
  onSelectMaterial,
  onSelectItem,
}: InventoryItemsModalGroupViewProps) => {
  const t = useTranslations();
  const isTagsMode = mode === 'tags';
  const itemFavoritesByGroup = useAtomValue($touchModalItemFavoritesByGroup);
  const itemOrderByGroup = useAtomValue($touchModalItemOrderByGroup);

  const itemsForDisplay = React.useMemo(() => {
    if (!isTagsMode) return itemsForGroup;
    return itemsForGroup.filter((item) =>
      inventoryTagOptionsByItemId?.has(item.id),
    );
  }, [isTagsMode, itemsForGroup, inventoryTagOptionsByItemId]);

  const groupColorHue = React.useMemo(
    () => itemsForDisplay[0]?.itemGroup?.colorHue,
    [itemsForDisplay],
  );
  const groupStyle = useInventoryGroupStyle({
    colorHue: groupColorHue ?? undefined,
  });
  const favoriteItemIds = React.useMemo(
    () => itemFavoritesByGroup[selectedGroupId] ?? [],
    [itemFavoritesByGroup, selectedGroupId],
  );
  const orderedItems = React.useMemo(
    () =>
      orderEntitiesByIds(
        itemsForDisplay,
        itemOrderByGroup[selectedGroupId] ??
          itemsForDisplay.map((item) => item.id),
      ),
    [itemsForDisplay, itemOrderByGroup, selectedGroupId],
  );

  const sortedItems = React.useMemo(() => {
    if (favoriteItemIds.length === 0) return orderedItems;
    const favSet = new Set(favoriteItemIds);
    const favItems = favoriteItemIds
      .map((id) => orderedItems.find((item) => item.id === id))
      .filter(Boolean) as InventoryItemRecord[];
    const rest = orderedItems.filter((item) => !favSet.has(item.id));
    return [...favItems, ...rest];
  }, [orderedItems, favoriteItemIds]);

  const favoriteSet = React.useMemo(
    () => new Set(favoriteItemIds),
    [favoriteItemIds],
  );

  const highlightedSet = React.useMemo(
    () => new Set(highlightedMaterialIds ?? []),
    [highlightedMaterialIds],
  );
  const hasHighlightedInGroup =
    highlightedSet.size > 0 &&
    sortedItems.some((item) => highlightedSet.has(item.id));
  const soItemsInGroup = React.useMemo(
    () =>
      hasHighlightedInGroup
        ? sortedItems.filter((item) => highlightedSet.has(item.id))
        : [],
    [sortedItems, highlightedSet, hasHighlightedInGroup],
  );
  const otherItemsInGroup = React.useMemo(
    () =>
      hasHighlightedInGroup
        ? sortedItems.filter((item) => !highlightedSet.has(item.id))
        : sortedItems,
    [sortedItems, highlightedSet, hasHighlightedInGroup],
  );
  const favoriteOtherItemsInGroup = React.useMemo(
    () => otherItemsInGroup.filter((item) => favoriteSet.has(item.id)),
    [otherItemsInGroup, favoriteSet],
  );
  const nonFavoriteOtherItemsInGroup = React.useMemo(
    () => otherItemsInGroup.filter((item) => !favoriteSet.has(item.id)),
    [otherItemsInGroup, favoriteSet],
  );
  const favoriteItemsInGroup = React.useMemo(
    () => sortedItems.filter((item) => favoriteSet.has(item.id)),
    [sortedItems, favoriteSet],
  );
  const nonFavoriteItemsInGroup = React.useMemo(
    () => sortedItems.filter((item) => !favoriteSet.has(item.id)),
    [sortedItems, favoriteSet],
  );

  const handleItemSelect = React.useCallback(
    (itemId: string) => {
      if (isTagsMode) {
        onSelectItem?.(itemId);
      } else {
        onSelectMaterial(itemId);
      }
    },
    [isTagsMode, onSelectItem, onSelectMaterial],
  );

  return (
    <div className="@InventoryItemsModalGroupView flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div
          style={groupStyle}
          className={cn(
            '@SelectedItemGroupIcon',
            'h-[44px] w-[35px] max-w-[35px] rounded-md border-2 border-neutral/20',
          )}
        />
        <div className="flex flex-col gap-1">
          <div className="text-sm text-neutral/60">
            {t.inventoryItemsModal.materialType}
          </div>
          <div className="text-xl font-medium">{selectedGroupName}</div>
        </div>
      </div>

      {hasHighlightedInGroup && (
        <>
          <div className="flex flex-col gap-2">
            <div className="text-sm font-semibold text-neutral/60">
              {highlightedSectionTitle}
            </div>
            <InventoryItemsModalItemsGrid
              items={soItemsInGroup}
              selectedId={selectedId}
              onSelectItem={handleItemSelect}
            />
          </div>
          {(favoriteOtherItemsInGroup.length > 0 ||
            nonFavoriteOtherItemsInGroup.length > 0) && (
            <hr className="m-0 border-t-2 border-neutral/15" />
          )}
          {favoriteOtherItemsInGroup.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-neutral/60">
                {t.scaleTicketPro.settings.favorites}
              </div>
              <InventoryItemsModalItemsGrid
                items={favoriteOtherItemsInGroup}
                selectedId={selectedId}
                onSelectItem={handleItemSelect}
              />
            </div>
          )}
          {favoriteOtherItemsInGroup.length > 0 &&
            nonFavoriteOtherItemsInGroup.length > 0 && (
              <hr className="m-0 border-t-2 border-neutral/15" />
            )}
          {nonFavoriteOtherItemsInGroup.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-neutral/60">
                {t.scaleTicketPro.inventoryItemField.allMaterials}
              </div>
              <InventoryItemsModalItemsGrid
                items={nonFavoriteOtherItemsInGroup}
                selectedId={selectedId}
                onSelectItem={handleItemSelect}
              />
            </div>
          )}
        </>
      )}
      {!hasHighlightedInGroup && (
        <>
          {favoriteItemsInGroup.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-neutral/60">
                {t.scaleTicketPro.settings.favorites}
              </div>
              <InventoryItemsModalItemsGrid
                items={favoriteItemsInGroup}
                selectedId={selectedId}
                onSelectItem={handleItemSelect}
              />
            </div>
          )}
          {favoriteItemsInGroup.length > 0 &&
            nonFavoriteItemsInGroup.length > 0 && (
              <hr className="m-0 border-t-2 border-neutral/15" />
            )}
          {nonFavoriteItemsInGroup.length > 0 && (
            <div className="flex flex-col gap-2">
              {favoriteItemsInGroup.length > 0 && (
                <div className="text-sm font-semibold text-neutral/60">
                  {t.scaleTicketPro.inventoryItemField.allMaterials}
                </div>
              )}
              <InventoryItemsModalItemsGrid
                items={
                  favoriteItemsInGroup.length > 0
                    ? nonFavoriteItemsInGroup
                    : sortedItems
                }
                selectedId={selectedId}
                onSelectItem={handleItemSelect}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
